/**
 * Gerenciamento de REPs (relógios de ponto).
 * Catálogo de fabricantes/modelos com configurações pré-definidas.
 * Credenciais de admin do REP nunca são armazenadas — apenas usadas in-flight.
 */
import { FormEvent, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { repApi, empresaApi, getErrorMessage, isPlanLimitError } from '../../lib/api'
import { PageLoading } from '../../components/LoadingSpinner'
import type { RepDevice } from '../../types/api'

// ── Catálogo de fabricantes e modelos ────────────────────────────────────────
// Apenas controlid tem driver implementado. Outros são cadastráveis mas sem
// coleta automática (driver a implementar em versões futuras).

type AfdMode = 'p671' | 'legado'

interface ModelConfig {
  model: string
  port: number
  afd_mode: AfdMode
  driver_ready: boolean
}

const VENDOR_CATALOG: Record<string, { label: string; models: ModelConfig[] }> = {
  controlid: {
    label: 'Control iD',
    models: [
      { model: 'iDClass', port: 443, afd_mode: 'p671', driver_ready: true },
      { model: 'iDFace', port: 443, afd_mode: 'p671', driver_ready: true },
      { model: 'iDAccess Pro', port: 443, afd_mode: 'p671', driver_ready: true },
      { model: 'iDBlock', port: 443, afd_mode: 'p671', driver_ready: true },
    ],
  },
  henry: {
    label: 'Henry',
    models: [
      { model: 'Prisma Facial', port: 80, afd_mode: 'legado', driver_ready: false },
      { model: 'Orion', port: 80, afd_mode: 'legado', driver_ready: false },
      { model: 'Seculus', port: 80, afd_mode: 'legado', driver_ready: false },
    ],
  },
  topdata: {
    label: 'Topdata',
    models: [
      { model: 'Inner Rep Plus', port: 80, afd_mode: 'legado', driver_ready: false },
      { model: 'Inner Rep Q', port: 80, afd_mode: 'legado', driver_ready: false },
    ],
  },
  dimep: {
    label: 'Dimep',
    models: [
      { model: 'D-Rep Evolution', port: 443, afd_mode: 'legado', driver_ready: false },
      { model: 'Kairos TS', port: 443, afd_mode: 'legado', driver_ready: false },
    ],
  },
  madis: {
    label: 'Madis',
    models: [
      { model: 'MR-1637 Facial', port: 80, afd_mode: 'legado', driver_ready: false },
      { model: 'MR-1648', port: 80, afd_mode: 'legado', driver_ready: false },
    ],
  },
}

const AFD_MODE_LABELS: Record<AfdMode, string> = {
  p671: 'Portaria 671/2021',
  legado: 'Legado 1510/595',
}

const FORM_EMPTY = {
  vendor: 'controlid',
  model: 'iDClass',
  descricao: '',
  empresa_id: '',
  numero_serie: '',
  numero_equipamento: '',
  local_equipamento: '',
  host: '',
  port: 443,
  afd_mode: 'p671' as AfdMode,
  cert_fingerprint: '',
  ativo: true,
}

type FormState = typeof FORM_EMPTY

export function RepsPage() {
  const qc = useQueryClient()
  const { data: reps, isLoading } = useQuery({ queryKey: ['reps'], queryFn: repApi.list })
  const { data: empresas } = useQuery({ queryKey: ['empresas'], queryFn: empresaApi.list })

  const [showForm, setShowForm] = useState(false)
  const [editRep, setEditRep] = useState<RepDevice | null>(null)
  const [form, setForm] = useState<FormState>(FORM_EMPTY)
  const [formError, setFormError] = useState<string | null>(null)

  const [testId, setTestId] = useState<string | null>(null)
  const [testCreds, setTestCreds] = useState({ username: 'admin', password: '' })
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null)

  const [syncId, setSyncId] = useState<string | null>(null)
  const [syncCreds, setSyncCreds] = useState({ username: 'admin', password: '' })
  const [syncResult, setSyncResult] = useState<{ synced: number; errors: string[] } | null>(null)

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        vendor: form.vendor,
        model: form.model,
        descricao: form.descricao || null,
        empresa_id: form.empresa_id || null,
        numero_serie: form.numero_serie || null,
        numero_equipamento: form.numero_equipamento ? Number(form.numero_equipamento) : null,
        local_equipamento: form.local_equipamento || null,
        host: form.host,
        port: Number(form.port),
        afd_mode: form.afd_mode,
        cert_fingerprint: form.cert_fingerprint || null,
        ativo: form.ativo,
      }
      return editRep ? repApi.update(editRep.id, payload) : repApi.create(payload)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['reps'] }); closeForm() },
    onError: (err) => {
      if (isPlanLimitError(err)) setFormError(getErrorMessage(err))
      else setFormError('Erro ao salvar o REP: ' + getErrorMessage(err))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => repApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reps'] }),
  })

  const testMutation = useMutation({
    mutationFn: () => repApi.testConnection(testId!, testCreds.username, testCreds.password),
    onSuccess: (r) => setTestResult(r),
    onError: (err) => setTestResult({ ok: false, message: getErrorMessage(err) }),
  })

  const syncMutation = useMutation({
    mutationFn: () => repApi.syncUsers(syncId!, syncCreds.username, syncCreds.password),
    onSuccess: (r) => setSyncResult(r),
    onError: (err) => setSyncResult({ synced: 0, errors: [getErrorMessage(err)] }),
  })

  function openCreate() {
    setEditRep(null); setForm(FORM_EMPTY); setFormError(null); setShowForm(true)
  }

  function openEdit(rep: RepDevice) {
    setEditRep(rep)
    setForm({
      vendor: rep.vendor,
      model: rep.model,
      descricao: rep.descricao ?? '',
      empresa_id: rep.empresa_id ?? '',
      numero_serie: rep.numero_serie ?? '',
      numero_equipamento: rep.numero_equipamento != null ? String(rep.numero_equipamento) : '',
      local_equipamento: rep.local_equipamento ?? '',
      host: rep.host,
      port: rep.port,
      afd_mode: rep.afd_mode,
      cert_fingerprint: rep.cert_fingerprint ?? '',
      ativo: rep.ativo,
    })
    setFormError(null)
    setShowForm(true)
  }

  function closeForm() { setShowForm(false); setEditRep(null) }

  // Quando troca fabricante: pre-seleciona primeiro modelo e ajusta configurações
  function handleVendorChange(vendor: string) {
    const firstModel = VENDOR_CATALOG[vendor]?.models[0]
    setForm(f => ({
      ...f,
      vendor,
      model: firstModel?.model ?? '',
      port: firstModel?.port ?? 80,
      afd_mode: firstModel?.afd_mode ?? 'legado',
    }))
  }

  // Quando troca modelo: ajusta port e afd_mode automaticamente
  function handleModelChange(model: string) {
    const cfg = VENDOR_CATALOG[form.vendor]?.models.find(m => m.model === model)
    setForm(f => ({
      ...f,
      model,
      port: cfg?.port ?? f.port,
      afd_mode: cfg?.afd_mode ?? f.afd_mode,
    }))
  }

  const currentModels = VENDOR_CATALOG[form.vendor]?.models ?? []
  const currentModelCfg = currentModels.find(m => m.model === form.model)

  const ativos = reps?.filter(r => r.ativo) ?? []
  const inativos = reps?.filter(r => !r.ativo) ?? []

  const empresaMap = Object.fromEntries((empresas ?? []).map(e => [e.id, e.nome]))

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-6)' }}>
        <div>
          <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700 }}>REPs Cadastrados</h1>
          <p style={{ color: 'var(--color-gray-600)', fontSize: 'var(--font-size-sm)' }}>
            Relógios de ponto integrados ao PontoFlow
          </p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Cadastrar REP</button>
      </div>

      {/* ── Modal de formulário ─────────────────────────────────────────────── */}
      {showForm && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}
          onClick={(e) => { if (e.target === e.currentTarget) closeForm() }}
        >
          <div className="card" style={{ width: '100%', maxWidth: 560, maxHeight: '92vh', overflowY: 'auto' }}>
            <h2 style={{ fontWeight: 600, marginBottom: 'var(--space-6)' }}>
              {editRep ? 'Editar REP' : 'Cadastrar REP'}
            </h2>
            {formError && (
              <div className="alert alert-danger" style={{ marginBottom: 'var(--space-4)' }}>{formError}</div>
            )}

            <form onSubmit={(e: FormEvent) => { e.preventDefault(); saveMutation.mutate() }} noValidate>

              {/* Seção: Identificação do Equipamento */}
              <SectionHeader>Identificação do Equipamento</SectionHeader>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                <Field label="Fabricante">
                  <select
                    className="input"
                    value={form.vendor}
                    onChange={e => handleVendorChange(e.target.value)}
                  >
                    {Object.entries(VENDOR_CATALOG).map(([key, v]) => (
                      <option key={key} value={key}>{v.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Modelo">
                  <select
                    className="input"
                    value={form.model}
                    onChange={e => handleModelChange(e.target.value)}
                  >
                    {currentModels.map(m => (
                      <option key={m.model} value={m.model}>{m.model}</option>
                    ))}
                  </select>
                </Field>
              </div>

              {currentModelCfg && !currentModelCfg.driver_ready && (
                <div className="alert alert-warning" style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-xs)' }}>
                  Driver de coleta automática para este modelo ainda não implementado. O cadastro é permitido para planejamento.
                </div>
              )}

              <Field label="Descrição" optional>
                <input
                  className="input"
                  value={form.descricao}
                  onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
                  placeholder="Ex: REP Portaria Principal"
                />
              </Field>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                <Field label="Número de Série" optional>
                  <input
                    className="input"
                    value={form.numero_serie}
                    onChange={e => setForm(f => ({ ...f, numero_serie: e.target.value }))}
                    placeholder="SN001234"
                  />
                </Field>
                <Field label="Nº do Equipamento" optional>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    value={form.numero_equipamento}
                    onChange={e => setForm(f => ({ ...f, numero_equipamento: e.target.value }))}
                    placeholder="1"
                  />
                </Field>
              </div>

              {/* Seção: Localização */}
              <SectionHeader>Localização</SectionHeader>

              <Field label="Empresa / Filial" optional>
                <select
                  className="input"
                  value={form.empresa_id}
                  onChange={e => setForm(f => ({ ...f, empresa_id: e.target.value }))}
                >
                  <option value="">— Sem empresa associada —</option>
                  {(empresas ?? []).filter(e => e.ativo).map(e => (
                    <option key={e.id} value={e.id}>{e.nome}</option>
                  ))}
                </select>
              </Field>

              <Field label="Local do Equipamento" optional>
                <input
                  className="input"
                  value={form.local_equipamento}
                  onChange={e => setForm(f => ({ ...f, local_equipamento: e.target.value }))}
                  placeholder="Ex: Portaria, Refeitório, Sala TI"
                />
              </Field>

              {/* Seção: Conexão */}
              <SectionHeader>Conexão de Rede</SectionHeader>

              <Field label="IP / Hostname do REP">
                <input
                  className="input"
                  value={form.host}
                  onChange={e => setForm(f => ({ ...f, host: e.target.value }))}
                  placeholder="192.168.1.100"
                  required
                />
              </Field>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                <Field label="Porta">
                  <input
                    className="input"
                    type="number"
                    value={form.port}
                    onChange={e => setForm(f => ({ ...f, port: Number(e.target.value) }))}
                    min={1} max={65535}
                  />
                </Field>
                <Field label="Modo AFD">
                  <select
                    className="input"
                    value={form.afd_mode}
                    onChange={e => setForm(f => ({ ...f, afd_mode: e.target.value as AfdMode }))}
                  >
                    <option value="p671">Portaria 671/2021 (recomendado)</option>
                    <option value="legado">Legado — Portaria 1510/595</option>
                  </select>
                </Field>
              </div>

              <Field label="Fingerprint do certificado (SHA-256)" optional>
                <input
                  className="input"
                  value={form.cert_fingerprint}
                  onChange={e => setForm(f => ({ ...f, cert_fingerprint: e.target.value }))}
                  placeholder="Deixe vazio para TOFU no primeiro acesso"
                  style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}
                />
              </Field>

              {/* Seção: Status */}
              <SectionHeader>Status</SectionHeader>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer', marginBottom: 'var(--space-4)' }}>
                <input
                  type="checkbox"
                  checked={form.ativo}
                  onChange={e => setForm(f => ({ ...f, ativo: e.target.checked }))}
                  style={{ width: 16, height: 16 }}
                />
                <span style={{ fontSize: 'var(--font-size-sm)' }}>REP ativo (coleta habilitada)</span>
              </label>

              <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
                <button type="submit" className="btn btn-primary" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Salvando…' : 'Salvar'}
                </button>
                <button type="button" className="btn btn-ghost" onClick={closeForm}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal de teste de conexão ───────────────────────────────────────── */}
      {testId && (
        <CredModal
          title="Testar conexão"
          creds={testCreds}
          onCredChange={setTestCreds}
          onSubmit={() => { setTestResult(null); testMutation.mutate() }}
          onClose={() => { setTestId(null); setTestResult(null) }}
          isPending={testMutation.isPending}
          submitLabel="Testar"
        >
          {testResult && (
            <div
              className={`alert ${testResult.ok ? 'alert-success' : 'alert-danger'}`}
              style={{ marginTop: 'var(--space-4)' }}
            >
              {testResult.ok ? '✓ ' : '✗ '}{testResult.message}
            </div>
          )}
        </CredModal>
      )}

      {/* ── Modal de sincronização ──────────────────────────────────────────── */}
      {syncId && (
        <CredModal
          title="Sincronizar funcionários ao REP"
          creds={syncCreds}
          onCredChange={setSyncCreds}
          onSubmit={() => { setSyncResult(null); syncMutation.mutate() }}
          onClose={() => { setSyncId(null); setSyncResult(null) }}
          isPending={syncMutation.isPending}
          submitLabel="Sincronizar"
        >
          {syncResult && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <div className="alert alert-success">{syncResult.synced} funcionários sincronizados.</div>
              {syncResult.errors.length > 0 && (
                <div className="alert alert-danger" style={{ marginTop: 'var(--space-2)' }}>
                  {syncResult.errors.join('; ')}
                </div>
              )}
            </div>
          )}
        </CredModal>
      )}

      {/* ── Lista de REPs ────────────────────────────────────────────────────── */}
      {isLoading ? <PageLoading /> : !reps?.length ? (
        <div className="card" style={{ textAlign: 'center', padding: 'var(--space-16)' }}>
          <div style={{ fontSize: '3rem', marginBottom: 'var(--space-4)' }}>⏱</div>
          <h3 style={{ fontWeight: 600, marginBottom: 'var(--space-2)' }}>Nenhum REP cadastrado</h3>
          <p style={{ color: 'var(--color-gray-600)', fontSize: 'var(--font-size-sm)' }}>
            Cadastre o primeiro relógio de ponto para começar a coletar marcações.
          </p>
        </div>
      ) : (
        <>
          {ativos.length > 0 && (
            <RepGrid
              reps={ativos}
              empresaMap={empresaMap}
              onEdit={openEdit}
              onTest={id => { setTestId(id); setTestResult(null) }}
              onSync={id => { setSyncId(id); setSyncResult(null) }}
              onDelete={id => { if (confirm('Remover este REP?')) deleteMutation.mutate(id) }}
            />
          )}
          {inativos.length > 0 && (
            <>
              <h3 style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)', marginTop: 'var(--space-8)', marginBottom: 'var(--space-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Inativos
              </h3>
              <RepGrid
                reps={inativos}
                empresaMap={empresaMap}
                onEdit={openEdit}
                onTest={id => { setTestId(id); setTestResult(null) }}
                onSync={id => { setSyncId(id); setSyncResult(null) }}
                onDelete={id => { if (confirm('Remover este REP?')) deleteMutation.mutate(id) }}
                dimmed
              />
            </>
          )}
        </>
      )}
    </div>
  )
}

function RepGrid({
  reps, empresaMap, onEdit, onTest, onSync, onDelete, dimmed,
}: {
  reps: RepDevice[]
  empresaMap: Record<string, string>
  onEdit: (r: RepDevice) => void
  onTest: (id: string) => void
  onSync: (id: string) => void
  onDelete: (id: string) => void
  dimmed?: boolean
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: 'var(--space-4)',
        opacity: dimmed ? 0.7 : 1,
      }}
    >
      {reps.map(rep => {
        const vendorLabel = VENDOR_CATALOG[rep.vendor]?.label ?? rep.vendor
        const afdLabel = AFD_MODE_LABELS[rep.afd_mode] ?? rep.afd_mode
        const empresaNome = rep.empresa_id ? empresaMap[rep.empresa_id] : null
        return (
          <div key={rep.id} className="card">
            {/* Cabeçalho */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-3)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 'var(--font-size-lg)', marginBottom: 2 }}>
                  {rep.descricao || rep.model}
                </div>
                {rep.descricao && (
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)' }}>
                    {vendorLabel} {rep.model}
                  </div>
                )}
                {!rep.descricao && (
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)', textTransform: 'uppercase' }}>
                    {vendorLabel}
                  </div>
                )}
              </div>
              <span className="badge badge-blue" style={{ marginLeft: 'var(--space-2)', flexShrink: 0 }}>
                {afdLabel}
              </span>
            </div>

            {/* Dados */}
            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-700)', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {empresaNome && (
                <div><span style={{ color: 'var(--color-gray-400)' }}>Empresa: </span>{empresaNome}</div>
              )}
              {rep.local_equipamento && (
                <div><span style={{ color: 'var(--color-gray-400)' }}>Local: </span>{rep.local_equipamento}</div>
              )}
              <div><span style={{ color: 'var(--color-gray-400)' }}>IP: </span>{rep.host}:{rep.port}</div>
              {rep.numero_serie && (
                <div><span style={{ color: 'var(--color-gray-400)' }}>Série: </span>{rep.numero_serie}</div>
              )}
              {rep.numero_equipamento != null && (
                <div><span style={{ color: 'var(--color-gray-400)' }}>Nº Equip.: </span>{rep.numero_equipamento}</div>
              )}
              <div><span style={{ color: 'var(--color-gray-400)' }}>Último NSR: </span>{rep.last_nsr}</div>
            </div>

            {rep.cert_fingerprint && (
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-400)', fontFamily: 'monospace', wordBreak: 'break-all', marginTop: 'var(--space-2)' }}>
                {rep.cert_fingerprint.slice(0, 32)}…
              </div>
            )}

            {/* Ações */}
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', marginTop: 'var(--space-4)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--color-gray-100)' }}>
              <button className="btn btn-ghost" style={{ fontSize: 'var(--font-size-xs)' }} onClick={() => onTest(rep.id)}>Testar</button>
              <button className="btn btn-ghost" style={{ fontSize: 'var(--font-size-xs)' }} onClick={() => onSync(rep.id)}>Sincronizar</button>
              <button className="btn btn-ghost" style={{ fontSize: 'var(--font-size-xs)' }} onClick={() => onEdit(rep)}>Editar</button>
              <button
                className="btn btn-ghost"
                style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)' }}
                onClick={() => onDelete(rep.id)}
              >
                Remover
              </button>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 'var(--font-size-xs)',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.07em',
      color: 'var(--color-gray-500)',
      borderBottom: '1px solid var(--color-gray-200)',
      paddingBottom: 'var(--space-2)',
      marginBottom: 'var(--space-4)',
      marginTop: 'var(--space-2)',
    }}>
      {children}
    </div>
  )
}

function Field({ label, children, optional }: { label: string; children: React.ReactNode; optional?: boolean }) {
  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 'var(--space-1)' }}>
        {label}{optional && <span style={{ color: 'var(--color-gray-400)', fontWeight: 400 }}> (opcional)</span>}
      </label>
      {children}
    </div>
  )
}

function CredModal({
  title, creds, onCredChange, onSubmit, onClose, isPending, submitLabel, children,
}: {
  title: string
  creds: { username: string; password: string }
  onCredChange: (c: { username: string; password: string }) => void
  onSubmit: () => void
  onClose: () => void
  isPending: boolean
  submitLabel: string
  children?: React.ReactNode
}) {
  return (
    <div
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="card" style={{ width: '100%', maxWidth: 400 }}>
        <h2 style={{ fontWeight: 600, marginBottom: 'var(--space-4)' }}>{title}</h2>
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)', marginBottom: 'var(--space-4)' }}>
          Credenciais de admin do REP. Usadas apenas nesta operação — nunca armazenadas.
        </p>
        <Field label="Usuário admin do REP">
          <input className="input" value={creds.username} onChange={e => onCredChange({ ...creds, username: e.target.value })} />
        </Field>
        <Field label="Senha admin do REP">
          <input className="input" type="password" value={creds.password} onChange={e => onCredChange({ ...creds, password: e.target.value })} />
        </Field>
        {children}
        <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
          <button className="btn btn-primary" onClick={onSubmit} disabled={isPending}>
            {isPending ? 'Aguarde…' : submitLabel}
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  )
}
